/**
 * MatchEntryPage — the Quick Entry flow.
 *
 * This is the single most-used page in the match module. Every interaction
 * is tuned for phone-first use: large tap targets, numeric keypad,
 * sticky submit bar at the bottom of the viewport.
 *
 * Key flows:
 *   1. Account picker
 *      - Mobile: bottom-sheet dialog with a search field.
 *      - Desktop: dropdown with the same search affordance.
 *      - Recently-used accounts (persisted in localStorage) surface first.
 *   2. Amount input
 *      - `inputmode="decimal"` triggers phone numpad.
 *      - INR / K-INR toggle lives in localStorage so it persists between
 *        sessions. Conversion is UI-only via `toInrString()` below.
 *   3. Live split preview
 *      - As soon as a source + amount are present we fetch the source's
 *        splits (or use the user's override) and preview every produced
 *        ledger row, including the remainder back to the source account.
 *   4. Override splits
 *      - Collapsible. First expansion is PIN-gated via `withPin`.
 *        The override inputs validate `0 < pct <= 100` and `sum <= 100`.
 *   5. Submit
 *      - Runs through `withPin` because the server may raise 423 if
 *        overrideSplits is present and the PIN unlock expired.
 *      - On success we keep the selected account (for rapid consecutive
 *        entries) and clear the amount + note fields.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Lock,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  ApiError,
  match,
  type MatchAccount,
  type MatchSplit,
} from '../api';
import { usePinGate } from '../auth/pinGate';
import { MatchShell } from './MatchShell';
import {
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  Pill,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Unit = 'INR' | 'KINR';

const UNIT_STORAGE_KEY = 'match-entry-unit';
const RECENT_ACCOUNTS_KEY = 'match-recent-accounts';
const MAX_RECENT = 5;

/**
 * Convert a user-typed amount + unit selection into a canonical INR string
 * suitable for the create-entry API. Returns an empty string for empty
 * input. Preserves sign, handles decimals of any length, and never leaves
 * a leading zero or trailing dot.
 *
 * Examples (unit = 'KINR'):
 *   "10"    → "10000"
 *   "10.5"  → "10500"
 *   "-2.5"  → "-2500"
 *   "0.123" → "123"
 *   "0.1234"→ "123.4"
 *
 * Examples (unit = 'INR'):
 *   "10500" → "10500"   (pass-through, trimmed)
 */
export function toInrString(raw: string, unit: Unit): string {
  const s = raw.trim();
  if (s.length === 0) return '';
  if (unit === 'INR') return s;
  const neg = s.startsWith('-');
  const abs = neg ? s.slice(1) : s;
  if (abs.length === 0) return '';
  const [intRaw = '0', fracRaw = ''] = abs.split('.');
  const intPart = intRaw.length === 0 ? '0' : intRaw;
  // Concatenate int + frac so we can shift the decimal purely by string math.
  const merged = intPart + fracRaw;
  const padded = merged + '0'.repeat(Math.max(0, 3 - fracRaw.length));
  const newFracLen = Math.max(0, fracRaw.length - 3);
  let result =
    newFracLen > 0
      ? padded.slice(0, -newFracLen) + '.' + padded.slice(-newFracLen)
      : padded;
  // Strip leading zeros from the integer part (but keep one).
  result = result.replace(/^0+(\d)/, '$1');
  // Trim trailing zeros after the decimal point, then a trailing dot if bare.
  if (result.includes('.')) {
    result = result.replace(/0+$/, '').replace(/\.$/, '');
  }
  if (result === '' || result === '-') return '0';
  return (neg ? '-' : '') + result;
}

/**
 * Parse a user-typed amount string into a JS number for preview math only.
 * Returns NaN for invalid input, 0 for empty. Exact math is done on the
 * server; this is strictly a display helper.
 */
function parsePreviewAmount(raw: string): number {
  const s = raw.trim();
  if (s.length === 0) return 0;
  if (!/^-?\d+(\.\d+)?$/.test(s)) return NaN;
  return Number(s);
}

/** Format a canonical INR decimal string with thousand-separators. */
function formatInr(raw: string): string {
  if (!raw) return '₹0';
  const neg = raw.startsWith('-');
  const abs = neg ? raw.slice(1) : raw;
  const [intPart, fracPart] = abs.split('.');
  const withCommas = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const formatted = fracPart ? `${withCommas}.${fracPart}` : withCommas;
  return `${neg ? '-' : ''}₹${formatted}`;
}

/** Format a preview JS number as INR with up to 3 fractional digits. */
function formatPreviewInr(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  // Keep up to 3 decimals to match server-side Decimal snapshot.
  const s = abs.toLocaleString('en-IN', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
  });
  return `${sign}₹${s}`;
}

function todayIso(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function readRecentAccounts(): number[] {
  try {
    const raw = window.localStorage.getItem(RECENT_ACCOUNTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is number => typeof v === 'number');
  } catch {
    return [];
  }
}

function writeRecentAccounts(ids: number[]) {
  try {
    window.localStorage.setItem(RECENT_ACCOUNTS_KEY, JSON.stringify(ids));
  } catch {
    // Storage unavailable — silent noop.
  }
}

function readUnit(): Unit {
  try {
    const raw = window.localStorage.getItem(UNIT_STORAGE_KEY);
    if (raw === 'KINR' || raw === 'INR') return raw;
  } catch {
    // ignore
  }
  return 'INR';
}

function writeUnit(u: Unit) {
  try {
    window.localStorage.setItem(UNIT_STORAGE_KEY, u);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Types local to this page
// ---------------------------------------------------------------------------

interface OverrideRow {
  /** Stable local id used as React key during editing. */
  localId: number;
  label: string;
  targetMatchAccountId: string; // stored as string so select binding is trivial
  percentage: string; // user-typed; validated on submit
}

interface PreviewRow {
  kind: 'split' | 'remainder';
  label: string;
  targetMatchAccountId: number;
  percentage: number; // JS number, preview only
  amount: number; // preview only
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MatchEntryPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { withPin } = usePinGate();

  // ---- Queries -----------------------------------------------------------
  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts-active'],
    queryFn: () => match.listAccounts({ status: 'active' }),
  });
  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);
  const accountsById = useMemo(
    () => new Map(accounts.map((a) => [a.id, a])),
    [accounts],
  );

  // ---- Form state --------------------------------------------------------
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [unit, setUnit] = useState<Unit>(() => readUnit());
  const [effectiveDate, setEffectiveDate] = useState<string>(() => todayIso());
  const [note, setNote] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideUnlocked, setOverrideUnlocked] = useState(false);
  const [overrideRows, setOverrideRows] = useState<OverrideRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => writeUnit(unit), [unit]);

  // Splits for the currently-selected source account.
  const splitsQ = useQuery<MatchSplit[]>({
    queryKey: ['match-splits', sourceId],
    queryFn: () =>
      sourceId === null
        ? Promise.resolve([])
        : match.getSplits(sourceId),
    enabled: sourceId !== null,
  });

  // ---- Derived: which splits drive the preview ---------------------------
  const previewRows = useMemo<PreviewRow[]>(() => {
    if (sourceId === null) return [];
    const previewAmount = parsePreviewAmount(amount);
    if (!Number.isFinite(previewAmount) || previewAmount === 0) return [];

    const usingOverride =
      overrideUnlocked && overrideOpen && overrideRows.length > 0;

    // Build rows from override inputs (preview only; invalid rows become 0).
    if (usingOverride) {
      const rows: PreviewRow[] = [];
      let sumPct = 0;
      for (const r of overrideRows) {
        const pct = Number(r.percentage);
        const targetId = Number(r.targetMatchAccountId);
        if (
          !Number.isFinite(pct) ||
          pct <= 0 ||
          pct > 100 ||
          !Number.isFinite(targetId) ||
          targetId === 0
        ) {
          continue;
        }
        sumPct += pct;
        rows.push({
          kind: 'split',
          label: r.label || '(unlabelled)',
          targetMatchAccountId: targetId,
          percentage: pct,
          amount: (previewAmount * pct) / 100,
        });
      }
      const remainderPct = Math.max(0, 100 - sumPct);
      if (remainderPct > 0) {
        // Compute remainder by subtraction for visual parity with server.
        const splitSum = rows.reduce((s, r) => s + r.amount, 0);
        rows.push({
          kind: 'remainder',
          label: 'Remainder',
          targetMatchAccountId: sourceId,
          percentage: remainderPct,
          amount: previewAmount - splitSum,
        });
      }
      return rows;
    }

    // Stored splits path.
    const splits = splitsQ.data ?? [];
    const rows: PreviewRow[] = [];
    let sumPct = 0;
    for (const s of splits) {
      const pct = Number(s.percentage);
      if (!Number.isFinite(pct) || pct <= 0) continue;
      sumPct += pct;
      rows.push({
        kind: 'split',
        label: s.label,
        targetMatchAccountId: s.targetMatchAccountId,
        percentage: pct,
        amount: (previewAmount * pct) / 100,
      });
    }
    const remainderPct = Math.max(0, 100 - sumPct);
    if (remainderPct > 0) {
      const splitSum = rows.reduce((s, r) => s + r.amount, 0);
      rows.push({
        kind: 'remainder',
        label: 'Remainder',
        targetMatchAccountId: sourceId,
        percentage: remainderPct,
        amount: previewAmount - splitSum,
      });
    }
    return rows;
  }, [
    sourceId,
    amount,
    overrideOpen,
    overrideRows,
    overrideUnlocked,
    splitsQ.data,
  ]);

  const previewSum = useMemo(
    () => previewRows.reduce((s, r) => s + r.amount, 0),
    [previewRows],
  );

  // ---- Mutations ---------------------------------------------------------
  const createMut = useMutation({
    mutationFn: (input: {
      sourceMatchAccountId: number;
      amount: string;
      effectiveDate: string;
      note?: string | null;
      overrideSplits?: Array<{
        targetMatchAccountId: number;
        label: string;
        percentage: string;
      }>;
    }) => match.createEntry(input),
    onSuccess: () => {
      // Keep selected source for rapid consecutive entries; reset volatile
      // fields. The toast lives long enough that the user notices.
      setAmount('');
      setNote('');
      setOverrideOpen(false);
      setOverrideRows([]);
      setOverrideUnlocked(false);
      setFormError(null);
      setFlash('Entry saved.');
      // Bump recents
      if (sourceId !== null) {
        const prev = readRecentAccounts().filter((id) => id !== sourceId);
        writeRecentAccounts([sourceId, ...prev].slice(0, MAX_RECENT));
      }
      void qc.invalidateQueries({ queryKey: ['match-balances'] });
      void qc.invalidateQueries({ queryKey: ['match-entries-recent'] });
      void qc.invalidateQueries({ queryKey: ['match-entries'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) {
        // Let the PIN gate retry silently — onSuccess handles the rest.
        return;
      }
      setFormError(e instanceof ApiError ? e.message : String(e));
    },
  });

  // Auto-dismiss the flash toast.
  useEffect(() => {
    if (!flash) return;
    const id = window.setTimeout(() => setFlash(null), 2500);
    return () => window.clearTimeout(id);
  }, [flash]);

  // ---- Submit handler ---------------------------------------------------
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (sourceId === null) {
      setFormError('Pick a source account first.');
      return;
    }

    const trimmedAmount = amount.trim();
    if (trimmedAmount.length === 0) {
      setFormError('Enter an amount.');
      return;
    }
    if (!/^-?\d+(\.\d+)?$/.test(trimmedAmount)) {
      setFormError('Amount must be a number (e.g. 10 or -2.5).');
      return;
    }
    if (Number(trimmedAmount) === 0) {
      setFormError('Amount cannot be zero.');
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
      setFormError('Pick a valid effective date.');
      return;
    }

    const trimmedNote = note.trim();
    if (trimmedNote.length > 500) {
      setFormError('Note must be 500 characters or fewer.');
      return;
    }

    let overrideSplits:
      | Array<{
          targetMatchAccountId: number;
          label: string;
          percentage: string;
        }>
      | undefined;
    if (overrideUnlocked && overrideOpen && overrideRows.length > 0) {
      overrideSplits = [];
      let sum = 0;
      for (const r of overrideRows) {
        const label = r.label.trim();
        if (label.length === 0) {
          setFormError('Every override split needs a label.');
          return;
        }
        const targetId = Number(r.targetMatchAccountId);
        if (!Number.isInteger(targetId) || targetId <= 0) {
          setFormError('Every override split needs a target account.');
          return;
        }
        const pct = Number(r.percentage);
        if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
          setFormError(
            'Override percentages must be greater than 0 and at most 100.',
          );
          return;
        }
        sum += pct;
        overrideSplits.push({
          targetMatchAccountId: targetId,
          label,
          percentage: String(pct),
        });
      }
      if (sum > 100) {
        setFormError(
          `Override split sum is ${sum}% — must total 100% or less.`,
        );
        return;
      }
    }

    const canonicalAmount = toInrString(trimmedAmount, unit);
    if (canonicalAmount === '' || canonicalAmount === '0' || canonicalAmount === '-0') {
      setFormError('Amount cannot be zero.');
      return;
    }

    void withPin(() =>
      createMut.mutateAsync({
        sourceMatchAccountId: sourceId,
        amount: canonicalAmount,
        effectiveDate,
        note: trimmedNote.length > 0 ? trimmedNote : null,
        overrideSplits,
      }),
    );
  };

  // ---- Override unlock handler ------------------------------------------
  const handleOverrideToggle = useCallback(() => {
    if (overrideOpen) {
      setOverrideOpen(false);
      return;
    }
    if (overrideUnlocked) {
      setOverrideOpen(true);
      return;
    }
    setOverrideUnlocked(true);
    setOverrideOpen(true);
    if (overrideRows.length === 0) {
      setOverrideRows([
        { localId: Date.now(), label: '', targetMatchAccountId: '', percentage: '' },
      ]);
    }
  }, [overrideOpen, overrideUnlocked, overrideRows.length]);

  // ---- Account picker: ordering + filtering ------------------------------
  const recentIds = readRecentAccounts();
  const orderedAccounts = useMemo(() => {
    const recentSet = new Set(recentIds);
    const recent = recentIds
      .map((id) => accountsById.get(id))
      .filter((a): a is MatchAccount => a !== undefined && a.status === 'active');
    const rest = accounts.filter(
      (a) => a.status === 'active' && !recentSet.has(a.id),
    );
    return [...recent, ...rest];
    // recentIds is read from localStorage each render; this is intentional
    // so that a submit (which writes recents) is reflected on the next
    // render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, accountsById]);

  const selectedAccount = sourceId !== null ? accountsById.get(sourceId) : null;

  const overrideAccountOptions = accounts.filter((a) => a.status === 'active');

  // ---- Render -----------------------------------------------------------
  if (accountsQ.isLoading) {
    return (
      <MatchShell>
        <div className="flex items-center gap-2 py-6 text-small text-ink-muted">
          <Spinner /> Loading accounts…
        </div>
      </MatchShell>
    );
  }

  if (accounts.length === 0) {
    return (
      <MatchShell>
        <EmptyState
          icon={AlertCircle}
          title="No active match accounts"
          description="Create at least one active match account before recording entries."
          action={
            <Button onClick={() => nav('/match/accounts')}>
              <Plus className="h-4 w-4" strokeWidth={2} />
              Manage accounts
            </Button>
          }
        />
      </MatchShell>
    );
  }

  const submitting = createMut.isPending;

  return (
    <MatchShell>
      <form onSubmit={onSubmit} className="relative pb-28 sm:pb-0">
        <div className="space-y-4">
          {/* Flash + error */}
          {flash && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-xl border border-pos/30 bg-pos/5 p-3 text-small text-pos"
            >
              <Check className="h-4 w-4" strokeWidth={2} />
              {flash}
            </div>
          )}
          {formError && <ErrorBanner>{formError}</ErrorBanner>}

          <div className="grid gap-4 md:grid-cols-2">
            {/* Column 1 — source picker */}
            <section className="space-y-2">
              <span className="text-small font-medium text-ink">
                Source account
              </span>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                aria-haspopup="dialog"
                className="flex w-full min-h-[56px] items-center justify-between rounded-xl border border-border bg-surface px-3 py-2 text-left text-body transition-colors hover:border-brand/40 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              >
                <span className="min-w-0 truncate">
                  {selectedAccount ? (
                    <span className="text-ink">
                      {selectedAccount.name}
                    </span>
                  ) : (
                    <span className="text-ink-muted">Tap to choose…</span>
                  )}
                </span>
                <ChevronDown
                  className="h-4 w-4 shrink-0 text-ink-muted"
                  strokeWidth={2}
                />
              </button>
              {selectedAccount?.notes && (
                <p className="text-caption text-ink-muted line-clamp-2">
                  {selectedAccount.notes}
                </p>
              )}
            </section>

            {/* Column 2 — amount + unit toggle */}
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="match-entry-amount"
                  className="text-small font-medium text-ink"
                >
                  Amount
                </label>
                <UnitToggle value={unit} onChange={setUnit} />
              </div>
              <input
                id="match-entry-amount"
                name="amount"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full min-h-[64px] rounded-xl border border-border bg-surface px-4 py-3 text-3xl font-mono tracking-tight text-ink placeholder:text-ink-muted transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
              <AmountHint amount={amount} unit={unit} />
            </section>
          </div>

          {/* Effective date + note */}
          <div className="grid gap-4 md:grid-cols-2">
            <TextInput
              label="Effective date"
              name="effectiveDate"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
            <div>
              <TextArea
                label={`Note (optional, ${note.length}/500)`}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 500))}
                placeholder="Optional context for this entry"
              />
            </div>
          </div>

          {/* Split preview */}
          <section className="space-y-2">
            <h2 className="text-h2 font-semibold text-ink">
              Split preview
            </h2>
            <Card>
              {sourceId === null ? (
                <p className="text-small text-ink-muted">
                  Pick a source account to preview its distribution.
                </p>
              ) : splitsQ.isLoading && !overrideUnlocked ? (
                <div className="flex items-center gap-2 text-small text-ink-muted">
                  <Spinner /> Loading splits…
                </div>
              ) : previewRows.length === 0 ? (
                <p className="text-small text-ink-muted">
                  {amount.trim() === ''
                    ? 'Enter an amount to preview the split.'
                    : 'This account has no splits — the whole amount stays on source.'}
                </p>
              ) : (
                <div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-small">
                      <thead>
                        <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-ink-muted">
                          <th className="px-2 py-2">Label</th>
                          <th className="px-2 py-2">Target</th>
                          <th className="px-2 py-2 text-right">%</th>
                          <th className="px-2 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {previewRows.map((r, i) => {
                          const target = accountsById.get(r.targetMatchAccountId);
                          return (
                            <tr
                              key={`${r.kind}-${r.label}-${i}`}
                              className={r.kind === 'remainder' ? 'bg-surface-raised/60' : ''}
                            >
                              <td className="px-2 py-2 text-ink">
                                {r.kind === 'remainder' ? (
                                  <span className="italic text-ink-muted">Remainder</span>
                                ) : (
                                  r.label
                                )}
                              </td>
                              <td className="px-2 py-2 text-ink">
                                {target?.name ?? `#${r.targetMatchAccountId}`}
                                {r.kind === 'remainder' && (
                                  <span className="ml-1 text-caption text-ink-muted">
                                    (source)
                                  </span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-right font-mono text-ink">
                                {r.percentage}%
                              </td>
                              <td
                                className={[
                                  'px-2 py-2 text-right font-mono',
                                  r.amount < 0 ? 'text-neg' : 'text-pos',
                                ].join(' ')}
                              >
                                {formatPreviewInr(r.amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-border">
                          <td className="px-2 py-2 text-caption uppercase tracking-wide text-ink-muted" colSpan={3}>
                            Total
                          </td>
                          <td className="px-2 py-2 text-right font-mono text-ink">
                            {formatPreviewInr(previewSum)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          </section>

          {/* Override splits */}
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-h2 font-semibold text-ink">
                Override splits
              </h2>
              <button
                type="button"
                onClick={handleOverrideToggle}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-raised px-3 py-2 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
              >
                {!overrideUnlocked && <Lock className="h-3.5 w-3.5" strokeWidth={2} />}
                {overrideOpen ? 'Hide override' : 'Override splits for this entry'}
              </button>
            </div>

            {overrideOpen && (
              <Card className="space-y-3">
                <div className="flex items-start gap-2 rounded-lg border border-brand/30 bg-brand/5 p-2 text-caption text-brand">
                  <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  <span>
                    Using per-entry override. The server requires a valid PIN
                    unlock — if yours has expired you&apos;ll be prompted when
                    you submit.
                  </span>
                </div>
                {overrideRows.map((row, idx) => (
                  <div
                    key={row.localId}
                    className="grid gap-2 rounded-lg border border-border bg-surface p-2 sm:grid-cols-[1fr_1fr_120px_44px]"
                  >
                    <TextInput
                      label={idx === 0 ? 'Label' : undefined}
                      value={row.label}
                      onChange={(e) =>
                        setOverrideRows((rs) =>
                          rs.map((r) =>
                            r.localId === row.localId
                              ? { ...r, label: e.target.value }
                              : r,
                          ),
                        )
                      }
                      placeholder="e.g. paati"
                    />
                    <div>
                      {idx === 0 && (
                        <span className="mb-1.5 block text-small font-medium text-ink">
                          Target
                        </span>
                      )}
                      <select
                        aria-label="Target account"
                        value={row.targetMatchAccountId}
                        onChange={(e) =>
                          setOverrideRows((rs) =>
                            rs.map((r) =>
                              r.localId === row.localId
                                ? { ...r, targetMatchAccountId: e.target.value }
                                : r,
                            ),
                          )
                        }
                        className="w-full min-h-[44px] rounded-xl border border-border bg-surface px-3 py-2.5 text-body text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                      >
                        <option value="">Choose…</option>
                        {overrideAccountOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <TextInput
                      label={idx === 0 ? '%' : undefined}
                      inputMode="decimal"
                      value={row.percentage}
                      onChange={(e) =>
                        setOverrideRows((rs) =>
                          rs.map((r) =>
                            r.localId === row.localId
                              ? { ...r, percentage: e.target.value }
                              : r,
                          ),
                        )
                      }
                      placeholder="0–100"
                    />
                    <div className={idx === 0 ? 'pt-[26px]' : ''}>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Remove override row"
                        onClick={() =>
                          setOverrideRows((rs) =>
                            rs.filter((r) => r.localId !== row.localId),
                          )
                        }
                        className="!min-h-[44px] !w-full !px-0"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={2} />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    setOverrideRows((rs) => [
                      ...rs,
                      {
                        localId: Date.now() + rs.length,
                        label: '',
                        targetMatchAccountId: '',
                        percentage: '',
                      },
                    ])
                  }
                >
                  <Plus className="h-4 w-4" strokeWidth={2} />
                  Add split row
                </Button>
              </Card>
            )}
          </section>

          {/* Desktop submit */}
          <div className="hidden items-center gap-2 sm:flex">
            <Button type="submit" disabled={submitting}>
              {submitting ? <Spinner /> : 'Save entry'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={submitting}
              onClick={() => {
                setAmount('');
                setNote('');
                setOverrideOpen(false);
                setOverrideRows([]);
                setOverrideUnlocked(false);
                setFormError(null);
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Mobile sticky submit bar */}
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 p-3 backdrop-blur sm:hidden">
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? <Spinner /> : 'Save entry'}
          </Button>
        </div>
      </form>

      {/* Account picker dialog / sheet */}
      {pickerOpen && (
        <AccountPickerDialog
          accounts={orderedAccounts}
          recentIds={recentIds}
          selectedId={sourceId}
          onClose={() => setPickerOpen(false)}
          onPick={(id) => {
            setSourceId(id);
            setPickerOpen(false);
          }}
        />
      )}
    </MatchShell>
  );
}

// ---------------------------------------------------------------------------
// Amount hint (below the large input)
// ---------------------------------------------------------------------------

function AmountHint({ amount, unit }: { amount: string; unit: Unit }) {
  const trimmed = amount.trim();
  if (trimmed.length === 0) {
    return (
      <p className="text-caption text-ink-muted">
        Tap to enter — use a leading <code className="rounded bg-surface-raised px-1">-</code> for
        negative entries.
      </p>
    );
  }
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return (
      <p className="text-caption text-neg">
        Not a valid number.
      </p>
    );
  }
  const n = Number(trimmed);
  if (n === 0) {
    return <p className="text-caption text-neg">Amount cannot be zero.</p>;
  }
  if (unit === 'KINR') {
    return (
      <p className="text-caption text-pos">
        = {formatInr(toInrString(trimmed, 'KINR'))} (will be saved as exact INR)
      </p>
    );
  }
  // INR mode — preview the K-INR label.
  const kinr = n / 1000;
  const sign = kinr < 0 ? '-' : '';
  const k = Math.abs(kinr).toLocaleString('en-IN', {
    maximumFractionDigits: 3,
    minimumFractionDigits: 0,
  });
  return (
    <p className="text-caption text-ink-muted">
      = {sign}
      {k} K-INR
    </p>
  );
}

// ---------------------------------------------------------------------------
// Unit toggle (segmented control)
// ---------------------------------------------------------------------------

function UnitToggle({
  value,
  onChange,
}: {
  value: Unit;
  onChange: (u: Unit) => void;
}) {
  const options: { value: Unit; label: string }[] = [
    { value: 'INR', label: 'INR' },
    { value: 'KINR', label: 'K-INR' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Amount unit"
      className="inline-flex rounded-lg border border-border bg-surface-raised p-0.5"
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={[
              'min-h-[36px] rounded-md px-3 py-1 text-caption font-medium transition-colors',
              active
                ? 'bg-brand text-white'
                : 'text-ink-muted hover:text-ink',
            ].join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account picker dialog / bottom sheet
// ---------------------------------------------------------------------------

function AccountPickerDialog({
  accounts,
  recentIds,
  selectedId,
  onClose,
  onPick,
}: {
  accounts: MatchAccount[];
  recentIds: number[];
  selectedId: number | null;
  onClose: () => void;
  onPick: (id: number) => void;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return accounts;
    return accounts.filter((a) => a.name.toLowerCase().includes(q));
  }, [accounts, query]);

  const recentSet = new Set(recentIds);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose source account"
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <div className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-elevated sm:max-w-lg sm:rounded-xl">
        <header className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-body font-semibold text-ink">Choose source account</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-surface-raised hover:text-ink"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </header>
        <div className="shrink-0 border-b border-border p-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
              strokeWidth={2}
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search accounts…"
              className="w-full min-h-[44px] rounded-xl border border-border bg-surface pl-9 pr-3 py-2 text-body text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
          </div>
        </div>
        <ul className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <li className="px-4 py-8 text-center text-small text-ink-muted">
              No accounts match &ldquo;{query}&rdquo;.
            </li>
          ) : (
            filtered.map((a) => {
              const isRecent = recentSet.has(a.id);
              const isSelected = a.id === selectedId;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => onPick(a.id)}
                    className={[
                      'flex w-full min-h-[56px] items-center justify-between gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-surface-raised',
                      isSelected ? 'bg-brand/5' : '',
                    ].join(' ')}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-body text-ink">
                          {a.name}
                        </span>
                        {isRecent && <Pill tone="indigo">Recent</Pill>}
                      </div>
                      {a.notes && (
                        <p className="mt-0.5 line-clamp-1 text-caption text-ink-muted">
                          {a.notes}
                        </p>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="h-4 w-4 shrink-0 text-brand" strokeWidth={2} />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
